import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, Download, Phone, Mail, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const BookingConfirmation = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchBookingDetails();
    fetchPaymentInfo();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const response = await axios.get(`${API}/public/bookings/${bookingId}`);
      if (response.data.success) {
        setBooking(response.data.booking);
        // Check if payment was already submitted
        if (response.data.booking.payment_screenshot_uploaded) {
          setPaymentSubmitted(true);
        }
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API}/payment/info`);
      if (response.data.success) {
        setPaymentInfo(response.data.payment_info);
      }
    } catch (error) {
      console.error('Error fetching payment info:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setPaymentScreenshot(file);
      setScreenshotPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentScreenshot) {
      toast.error('Please upload your payment screenshot');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('screenshot', paymentScreenshot);
      formData.append('booking_id', bookingId);

      const response = await axios.post(`${API}/public/upload-payment`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        toast.success('Payment screenshot uploaded successfully! Admin will verify and confirm your booking.');
        setPaymentSubmitted(true);
      }
    } catch (error) {
      console.error('Error uploading payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload payment screenshot');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!booking) {
    return <div className="min-h-screen flex items-center justify-center">Booking not found</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Toaster />
      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-green-700 text-white py-8 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_homestay-manager-1/artifacts/3ar6f175_Primary%20on%20transparent.png" 
              alt="Himali Khim Homestay" 
              className="h-16 w-auto"
            />
            <div>
              <h1 className="text-3xl font-bold">Booking Confirmation</h1>
              <p className="text-green-100 mt-1">Your booking has been received!</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Success Message */}
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardContent className="flex items-center gap-4 p-6">
            <CheckCircle className="text-green-600" size={48} />
            <div>
              <h2 className="text-2xl font-bold text-green-800">Booking Request Submitted!</h2>
              <p className="text-green-700 mt-1">
                Booking ID: <span className="font-mono font-bold">{booking.id}</span>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Booking Details */}
          <Card>
            <CardHeader>
              <CardTitle>Booking Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Room</p>
                <p className="font-semibold">{booking.room_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Check-in</p>
                  <p className="font-semibold">{booking.check_in}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Check-out</p>
                  <p className="font-semibold">{booking.check_out}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Duration</p>
                <p className="font-semibold">{booking.num_nights} night(s)</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Guests</p>
                <p className="font-semibold">{booking.num_guests} guest(s)</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Meal Plan</p>
                <p className="font-semibold">{booking.meal_plan === 'room_only' ? 'Room Only' : 'Room + 4 Meals'}</p>
              </div>
              <div className="border-t pt-3">
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-gray-700">₹{booking.total_amount}</p>
              </div>
              <div className="border-t pt-3 bg-green-50 -mx-6 px-6 pb-4 mt-3">
                <p className="text-sm text-green-700 font-medium">Advance Payment (50%)</p>
                <p className="text-3xl font-bold text-green-700">₹{Math.round(booking.total_amount * 0.5)}</p>
                <p className="text-xs text-green-600 mt-1">Balance ₹{Math.round(booking.total_amount * 0.5)} to be paid at check-in</p>
              </div>
            </CardContent>
          </Card>

          {/* Guest Details */}
          <Card>
            <CardHeader>
              <CardTitle>Guest Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-semibold">{booking.guest_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold">{booking.guest_email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-semibold">{booking.guest_phone}</p>
              </div>
              {booking.special_requests && (
                <div>
                  <p className="text-sm text-gray-600">Special Requests</p>
                  <p className="text-sm">{booking.special_requests}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment Information */}
        {paymentInfo && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-green-700">Complete Your Payment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* UPI Payment */}
                <div>
                  <h3 className="font-semibold mb-3">Option 1: Pay via UPI</h3>
                  <div className="flex justify-center bg-white p-4 rounded-lg border mb-3">
                    <img 
                      src={paymentInfo.upi_qr_url}
                      alt="UPI QR Code"
                      className="w-48 h-48 object-contain"
                    />
                  </div>
                  <p className="text-center text-sm text-gray-600">Scan with any UPI app (Google Pay, PhonePe, Paytm)</p>
                </div>

                {/* Bank Transfer */}
                <div>
                  <h3 className="font-semibold mb-3">Option 2: Bank Transfer</h3>
                  <div className="space-y-3">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Account Holder</p>
                      <p className="font-semibold">{paymentInfo.account_holder}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Account Number</p>
                      <p className="font-mono font-semibold">{paymentInfo.account_number}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">IFSC Code</p>
                      <p className="font-mono font-semibold">{paymentInfo.ifsc_code}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Bank Name</p>
                      <p className="font-semibold">{paymentInfo.bank_name}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Screenshot Upload */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <Upload size={20} />
                  Upload Payment Screenshot
                </h3>
                
                {paymentSubmitted ? (
                  <div className="text-center py-4">
                    <CheckCircle className="text-green-600 mx-auto mb-2" size={48} />
                    <p className="text-green-700 font-semibold">Payment Screenshot Submitted!</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Admin will verify your payment and confirm your booking. You will receive a confirmation email shortly.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-blue-700 mb-3">
                      After making payment, upload your payment screenshot here. Admin will verify and confirm your booking.
                    </p>
                    
                    <div className="space-y-3">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="bg-white"
                        data-testid="payment-screenshot-input"
                      />
                      
                      {screenshotPreview && (
                        <div className="flex justify-center">
                          <img 
                            src={screenshotPreview} 
                            alt="Payment Screenshot Preview" 
                            className="max-w-xs max-h-48 object-contain border rounded-lg"
                          />
                        </div>
                      )}
                      
                      <Button
                        onClick={handleSubmitPayment}
                        disabled={uploading || !paymentScreenshot}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        data-testid="submit-payment-btn"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="mr-2 h-4 w-4" />
                            Submit Payment Screenshot
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-6 bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-sm font-semibold text-yellow-800 mb-2">Important:</p>
                <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                  <li>Pay advance of <strong>₹{Math.round(booking.total_amount * 0.5)}</strong> (50% of ₹{booking.total_amount}) to confirm your booking</li>
                  <li>Remaining <strong>₹{Math.round(booking.total_amount * 0.5)}</strong> to be paid at check-in</li>
                  <li>Upload payment screenshot above for quick verification</li>
                  <li>Your booking will be confirmed once payment is verified</li>
                  <li>You will receive a confirmation email after admin approval</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Information */}
        <Card className="mt-6 bg-gradient-to-r from-green-50 to-blue-50">
          <CardHeader>
            <CardTitle>Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">Contact us for any queries:</p>
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                <Phone size={20} className="text-green-600" />
                <div>
                  <p className="text-xs text-gray-500">Call Us</p>
                  <a href="tel:7029069077" className="font-semibold text-green-700 hover:underline">
                    +91 7029069077
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                <Mail size={20} className="text-green-600" />
                <div>
                  <p className="text-xs text-gray-500">Email Us</p>
                  <a href="mailto:himalikhim@gmail.com" className="font-semibold text-green-700 hover:underline">
                    himalikhim@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Button 
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="mr-3"
          >
            ← Back to Home
          </Button>
          <Button 
            onClick={() => window.print()}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download size={16} />
            Print Confirmation
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
